import { cn } from '@syncanix/ui';

import { renderSidebarItem, type SidebarItem } from './SidebarNavItem';

import type { ReactElement } from 'react';

// Re-exported so `@/components/Sidebar/Sidebar` stays the module every consumer
// imports from — the split below is internal.
export { COUNT_OVERFLOW, type SidebarItem } from './SidebarNavItem';

export interface SidebarProps {
  items: readonly SidebarItem[];
  collapsed?: boolean;
  /**
   * Localized accessible name for the `<nav>` landmark. REQUIRED — the caller
   * owns the (translated) string; the component ships no English default so a
   * hard-coded "Sidebar" can never leak into a non-English UI.
   */
  ariaLabel: string;
  className?: string;
}

const COLLAPSED_WIDTH = 'w-sidebar-collapsed';
const EXPANDED_WIDTH = 'w-sidebar';

/**
 * Partition items into the leading ungrouped flat list and a Map of groups
 * keyed by group label in first-occurrence order.
 */
function partition(items: readonly SidebarItem[]): {
  ungrouped: readonly SidebarItem[];
  groups: ReadonlyMap<string, readonly SidebarItem[]>;
} {
  const ungrouped: SidebarItem[] = [];
  const groups = new Map<string, SidebarItem[]>();
  for (const item of items) {
    if (item.group === undefined) {
      ungrouped.push(item);
      continue;
    }
    const existing = groups.get(item.group);
    if (existing) {
      existing.push(item);
    } else {
      groups.set(item.group, [item]);
    }
  }
  return { ungrouped, groups };
}

/**
 * Vertical primary navigation. Dense, Linear-style. NavLink active state
 * matches the route prefix; the icons stay column-aligned in both expanded
 * and collapsed states so the user can scan the column without rescanning
 * positions when the sidebar collapses.
 *
 * Phase 8 Pass 3 adds optional grouping. Items declaring `group` render
 * under a labelled region (`role="group"` + `aria-label`); the visible
 * heading is uppercase + tracking-wide for the AGENT / SURFACES / PLATFORM
 * categories the product spec specifies. Collapsed mode preserves the
 * group landmarks for screen readers but hides the visible heading glyph.
 */
export function Sidebar({
  items,
  collapsed = false,
  ariaLabel,
  className,
}: SidebarProps): ReactElement {
  const { ungrouped, groups } = partition(items);

  return (
    <nav
      aria-label={ariaLabel}
      className={cn(
        // overflow-y-auto: with 6 groups the nav outgrows short viewports
        // (375x812 drawer clipped ORG & SETTINGS entirely — A17 finding);
        // the nav must scroll independently of the main region.
        // SYN-31: the rail is a `surface` panel (distinct from the `background`
        // canvas the content area paints) with a logical hairline divider.
        // Bedrock rail: a hairline-ruled surface, not a floated panel — it is
        // part of the same sheet as the page beside it.
        'flex h-full shrink-0 flex-col gap-0.5 overflow-y-auto border-e border-hairline bg-surface py-2.5 text-sm',
        collapsed ? `${COLLAPSED_WIDTH} px-2` : `${EXPANDED_WIDTH} px-2.5`,
        className,
      )}
    >
      {ungrouped.length > 0 ? (
        <ul className="flex flex-col gap-0.5">
          {ungrouped.map((item) => renderSidebarItem(item, collapsed))}
        </ul>
      ) : null}

      {Array.from(groups.entries()).map(([groupLabel, groupItems]) => (
        <div
          key={groupLabel}
          role="group"
          aria-label={groupLabel}
          className="group mt-3 flex flex-col gap-0.5 first:mt-0"
        >
          {collapsed ? (
            // Icon-only rail: the caption is hidden, so a hairline stands in as
            // the visible group boundary — without it the groups are separated
            // only by invisible whitespace and the rail reads as one long
            // unstructured icon column. `group-first:hidden`: a boundary needs
            // something ABOVE it — when the group opens the rail (no ungrouped
            // items before it) a leading hairline would separate nothing.
            <div
              data-testid="sidebar-group-separator"
              aria-hidden="true"
              className="mx-1.5 mb-1.5 h-px bg-hairline group-first:hidden"
            />
          ) : (
            // SYN-31: monospace group caption (the design's `.grouplabel`).
            // Full muted-foreground tone (no /80) so the caption clears WCAG AA
            // 4.5:1 on the `surface` rail; the mono + uppercase + 11px treatment
            // keeps it visually distinct from the nav labels without dimming.
            // Letter-spacing is a Latin-only treatment: Arabic/Hebrew are never
            // tracked (Arabic is a connected script), so RTL resets it.
            <div className="px-2.5 pb-1 pt-2.5 font-mono text-label uppercase text-tertiary">
              {groupLabel}
            </div>
          )}
          <ul className="flex flex-col gap-0.5">
            {groupItems.map((item) => renderSidebarItem(item, collapsed))}
          </ul>
        </div>
      ))}
    </nav>
  );
}
